import { useUser } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../../../convex/_generated/api";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { DashboardCard } from "@/components/ui/dashboard-card";
import { useOrganization } from "@clerk/clerk-react";
import {
  Users,
  Calendar,
  DollarSign,
  CheckCircle,
  Clock,
  AlertTriangle,
  FileText,
  Activity,
  Briefcase,
  Building2,
  Star,
  CheckCircle2,
  StarHalf,
  BrushCleaning,
  FileEdit,
  MapPin,
} from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatPercentage } from "@/lib/utils";
import { EmptyState } from "@/components/ui/empty-state";

export default function DashboardOverview() {
  const { user } = useUser();
  const navigate = useNavigate();
  const userData = useQuery(
    api.users.getUserByToken,
    user?.id ? { tokenIdentifier: user.id } : "skip"
  );

  const { organization: clerkOrganization, membership } = useOrganization();
  const organizationQuery = useQuery(api.organizations.getByUser, user ? {} : "skip");
  const personalWorkspace = useQuery(api.personalWorkspaces.getByUser, user ? {} : "skip");

  // Fetch dashboard analytics
  const dashboardData = useQuery(api.businessAnalytics.getBusinessDashboard);
  const upcomingProjects = useQuery(api.businessAnalytics.getUpcomingServiceProjects, { limit: 5 });

  // Loading state
  const isLoading = dashboardData === undefined || upcomingProjects === undefined;

  return (
    <DashboardLayout activeTab="overview">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Business Overview</h1>
          <div className="flex items-center space-x-4">
            {isLoading && (
              <div className="flex items-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-500">Loading...</span>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Active Clients</h3>
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <div className="mb-2 text-2xl font-bold">
              {isLoading ? (
                <div className="h-8 w-16 animate-pulse rounded bg-gray-200"></div>
              ) : (
                dashboardData?.overview.totalClients || 0
              )}
            </div>
            {!isLoading && dashboardData && (
              <div
                className={`flex items-center text-sm ${
                  dashboardData.trends.clientTrend >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                <span>
                  {dashboardData.trends.clientTrend >= 0 ? "↑" : "↓"}{" "}
                  {formatPercentage(Math.abs(dashboardData.trends.clientTrend), 1)}
                </span>
                <span className="ml-2 text-gray-500">from last month</span>
              </div>
            )}
            {isLoading && <div className="mt-2 h-4 w-24 animate-pulse rounded bg-gray-200"></div>}
          </Card>

          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Bids Won This Month</h3>
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div className="mb-2 text-2xl font-bold">
              {isLoading ? (
                <div className="h-8 w-24 animate-pulse rounded bg-gray-200"></div>
              ) : (
                formatCurrency(dashboardData?.trends.monthlyRevenue || 0)
              )}
            </div>
            {!isLoading && dashboardData && (
              <div
                className={`flex items-center text-sm ${
                  dashboardData.trends.revenueTrend >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                <span>
                  {dashboardData.trends.revenueTrend >= 0 ? "↑" : "↓"}{" "}
                  {formatPercentage(Math.abs(dashboardData.trends.revenueTrend), 1)}
                </span>
                <span className="ml-2 text-gray-500">from last month</span>
              </div>
            )}
            {isLoading && <div className="mt-2 h-4 w-24 animate-pulse rounded bg-gray-200"></div>}
          </Card>

          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Leads in Pipeline</h3>
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="mb-2 text-2xl font-bold">
              {isLoading ? (
                <div className="h-8 w-16 animate-pulse rounded bg-gray-200"></div>
              ) : (
                dashboardData?.overview.totalLeads || 0
              )}
            </div>
            {!isLoading && dashboardData && (
              <div
                className={`flex items-center text-sm ${
                  dashboardData.trends.leadTrend >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                <span>
                  {dashboardData.trends.leadTrend >= 0 ? "↑" : "↓"}{" "}
                  {formatPercentage(Math.abs(dashboardData.trends.leadTrend), 1)}
                </span>
                <span className="ml-2 text-gray-500">from last month</span>
              </div>
            )}
            {isLoading && <div className="mt-2 h-4 w-24 animate-pulse rounded bg-gray-200"></div>}
          </Card>

          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Client Ratings</h3>
              <StarHalf className="h-6 w-6 text-blue-600" />
            </div>
            <div className="mb-2 text-2xl font-bold">
              {isLoading ? (
                <div className="h-8 w-20 animate-pulse rounded bg-gray-200"></div>
              ) : dashboardData ? (
                dashboardData.overview.avgClientRating > 0 ? (
                  `${dashboardData.overview.avgClientRating.toFixed(1)}/5`
                ) : (
                  "No ratings"
                )
              ) : (
                "No ratings"
              )}
            </div>
            {!isLoading && dashboardData && dashboardData.overview.avgClientRating > 0 && (
              <div
                className={`flex items-center text-sm ${
                  dashboardData.trends.ratingTrend >= 0 ? "text-green-600" : "text-red-600"
                }`}
              >
                <span>
                  {dashboardData.trends.ratingTrend >= 0 ? "↑" : "↓"}{" "}
                  {formatPercentage(Math.abs(dashboardData.trends.ratingTrend), 1)}
                </span>
                <span className="ml-2 text-gray-500">from last month</span>
              </div>
            )}
            {isLoading && <div className="mt-2 h-4 w-24 animate-pulse rounded bg-gray-200"></div>}
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card className="p-6">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Recent Bids</h3>
              <Button
                variant="ghost"
                className="text-sm text-blue-600"
                onClick={() => navigate("/bids")}
              >
                View All
              </Button>
            </div>
            <div className="space-y-4">
              {dashboardData && dashboardData.recentActivity.recentBids.length > 0 ? (
                dashboardData.recentActivity.recentBids.map((bid: any) => (
                  <div
                    key={bid._id}
                    onClick={() => navigate(`/bids/${bid._id}`)}
                    className="flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 font-medium">
                        <FileText className="h-4 w-4 text-blue-500" />
                        {bid.title}
                      </div>
                      <div className="text-sm text-gray-500">{bid.client}</div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                          bid.status === "Approved"
                            ? "bg-green-100 text-green-700"
                            : bid.status === "Submitted"
                              ? "bg-blue-100 text-blue-700"
                              : bid.status === "In Review"
                                ? "bg-yellow-100 text-yellow-700"
                                : bid.status === "Rejected"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {bid.status}
                      </span>
                      <div className="text-right">
                        <div className="font-medium">{bid.value}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(bid.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <EmptyState
                  icon={FileEdit}
                  title="No recent bids found"
                  description="Create your first bid to see it here"
                />
              )}
            </div>
          </Card>

          <Card className="p-6">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Activity Timeline</h3>
              <Activity className="h-5 w-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              {dashboardData && dashboardData.recentActivity.timeline.length > 0 ? (
                dashboardData.recentActivity.timeline.map((activity: any) => {
                  const getIcon = (iconType: string) => {
                    switch (iconType) {
                      case "FileText":
                        return <FileText className="h-4 w-4 text-blue-500" />;
                      case "Building2":
                        return <Building2 className="h-4 w-4 text-purple-500" />;
                      case "CheckCircle2":
                        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
                      default:
                        return <Activity className="h-4 w-4 text-gray-500" />;
                    }
                  };

                  const getTimeAgo = (timestamp: number) => {
                    const now = Date.now();
                    const diff = now - timestamp;
                    const hours = Math.floor(diff / (1000 * 60 * 60));
                    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

                    if (days > 0) {
                      return days === 1 ? "Yesterday" : `${days} days ago`;
                    } else if (hours > 0) {
                      return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
                    } else {
                      return "Just now";
                    }
                  };

                  return (
                    <div key={activity.id} className="flex gap-3">
                      <div className="mt-0.5">{getIcon(activity.icon)}</div>
                      <div className="space-y-1">
                        <div className="font-medium">{activity.title}</div>
                        <div className="text-sm text-gray-600">{activity.description}</div>
                        <div className="text-xs text-gray-500">{getTimeAgo(activity.time)}</div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <EmptyState
                  icon={Activity}
                  title="No recent activity"
                  description="Activity will appear here as you use the platform"
                />
              )}
            </div>
          </Card>
        </div>

        {/* Service Projects (Expanded to full width) */}
        <div className="grid grid-cols-1 gap-6">
          {/* Upcoming Service Projects */}
          <Card className="p-6">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Upcoming Service Projects</h3>
              <Button
                variant="ghost"
                className="text-sm text-blue-600"
                onClick={() => navigate("/project-schedules")}
              >
                View All
              </Button>
            </div>
            <div className="space-y-4">
              {upcomingProjects && upcomingProjects.length > 0 ? (
                upcomingProjects.map((project: any) => (
                  <div
                    key={project._id}
                    onClick={() => navigate("/project-schedules")}
                    className="flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 font-medium">
                        <BrushCleaning className="h-4 w-4 text-blue-500" />
                        {project.title}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Building2 className="h-3 w-3" />
                        <span>{project.client}</span>
                        <span className="px-1">•</span>
                        <MapPin className="h-3 w-3" />
                        <span>{project.location}</span>
                        <span className="px-1">•</span>
                        <Calendar className="h-3 w-3" />
                        <span>{new Date(project.date).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                          project.status === "completed"
                            ? "bg-gray-100 text-gray-700"
                            : project.status === "in progress"
                              ? "bg-blue-100 text-blue-700"
                              : project.status === "scheduled"
                                ? "bg-green-100 text-green-700"
                                : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {project.status === "completed"
                          ? "Completed"
                          : project.status === "in progress"
                            ? "In Progress"
                            : project.status === "scheduled"
                              ? "Scheduled"
                              : "Pending"}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <EmptyState
                  icon={BrushCleaning}
                  title="No upcoming service projects"
                  description="Schedule your first service to see it here"
                />
              )}
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

interface ActivityItemProps {
  title: string;
  description: string;
  time: string;
  icon: React.ReactNode;
}

function ActivityItem({ title, description, time, icon }: ActivityItemProps) {
  return (
    <div className="flex items-start space-x-3">
      <div className="rounded-full bg-gray-50 p-2">{icon}</div>
      <div className="flex-1">
        <h3 className="text-sm font-medium">{title}</h3>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <span className="text-xs text-gray-400">{time}</span>
    </div>
  );
}

interface UpcomingServiceProps {
  client: string;
  service: string;
  date: string;
  status: "scheduled" | "attention" | "completed";
  note?: string;
}

function UpcomingService({ client, service, date, status, note }: UpcomingServiceProps) {
  const statusColors = {
    scheduled: "text-blue-600 bg-blue-50",
    attention: "text-amber-600 bg-amber-50",
    completed: "text-green-600 bg-green-50",
  };

  const statusIcons = {
    scheduled: <Clock className="h-4 w-4" />,
    attention: <AlertTriangle className="h-4 w-4" />,
    completed: <CheckCircle className="h-4 w-4" />,
  };

  return (
    <div className="flex items-start space-x-3">
      <div className={`rounded-full p-2 ${statusColors[status]}`}>{statusIcons[status]}</div>
      <div className="flex-1">
        <h3 className="text-sm font-medium">{client}</h3>
        <p className="text-sm text-gray-500">{service}</p>
        {note && <p className="mt-1 text-xs text-amber-600">{note}</p>}
      </div>
      <span className="text-xs text-gray-400">{date}</span>
    </div>
  );
}

interface PipelineStageProps {
  title: string;
  count: number;
  color: string;
}

function PipelineStage({ title, count, color }: PipelineStageProps) {
  return (
    <div className={`${color} rounded-lg p-4 text-center`}>
      <h3 className="text-sm font-medium text-gray-800">{title}</h3>
      <p className="mt-2 text-2xl font-bold">{count}</p>
    </div>
  );
}
